// Per-role destination registry.
// Primary bars are the visible QuickBooks-style set. Every other route lives
// in the More sheet or on the Admin page. OWNER and ACCOUNT_MANAGER stay
// separate so a later lane can append on its own lines.

import type { RouteName, StaffRole } from "./types.js";

export type ShellRoute = RouteName | "system";

export type NavDestination = {
  id: ShellRoute;
  label: string;
  glyph: string;
};

export type MoreGroup = {
  heading: string;
  destinations: NavDestination[];
};

/** GL-21 "Import prior bookings" for AM and Owner. */
export const PRIOR_IMPORT_NAV: NavDestination = {
  id: "prior-import",
  label: "Import prior bookings",
  glyph: "↑",
};

/** GL-27 "Compare" (GV Books vs the other software vs AI) for AM and Owner. */
export const COMPARE_NAV: NavDestination = {
  id: "compare",
  label: "Compare",
  glyph: "⇋",
};

const HOME: NavDestination = { id: "home", label: "Home", glyph: "home" };
const TO_REVIEW: NavDestination = { id: "queue", label: "To review", glyph: "review" };
const ACTIVITY: NavDestination = { id: "transactions", label: "Activity", glyph: "activity" };
const BOOKS: NavDestination = { id: "general-ledger", label: "Books", glyph: "books" };
const REPORTS: NavDestination = { id: "reports", label: "Reports", glyph: "reports" };
/**
 * rd15 A9: the 1099 totals download. It lives under Reports in the More sheet
 * for both roles: reading a saved window is owner or account manager, and the
 * page writes nothing.
 */
const TAX_TIME: NavDestination = { id: "tax-time", label: "Tax time", glyph: "$" };
// G8: is a month closed, reconciled, or what is blocking it? Reports, after
// Reports and Books; not in the phone bottom bar.
const MONTHS: NavDestination = { id: "months", label: "Months", glyph: "▦" };
// Item 8: the top-level Accounts tab is the bank accounts page; the Books
// "Accounts" sub-tab keeps that name for the chart of accounts.
const BANK_ACCOUNTS: NavDestination = { id: "balances", label: "Bank accounts", glyph: "accounts" };

const RECEIPTS: NavDestination = {
  id: "receipt-shelf",
  label: "Receipts and bills",
  glyph: "▤",
};
const RECURRING: NavDestination = { id: "recurring", label: "Recurring", glyph: "↻" };
const INTERCOMPANY: NavDestination = {
  id: "intercompany",
  label: "Money between companies",
  glyph: "⇄",
};
const WAITING: NavDestination = { id: "waiting", label: "Waiting", glyph: "◌" };
const PROBLEMS: NavDestination = { id: "errors", label: "Problems", glyph: "!" };
const STATEMENT_CHECKS: NavDestination = {
  id: "statements",
  label: "Statement checks",
  glyph: "▥",
};
const RULES: NavDestination = { id: "rules", label: "Rules", glyph: "⚙" };
const CONNECTION_STATUS: NavDestination = {
  id: "health",
  label: "Connection status",
  glyph: "♥",
};
const ADMIN: NavDestination = { id: "system", label: "Admin", glyph: "⚙" };
/** Hidden compatibility control: the old System tab's accessible name. */
const LEGACY_SYSTEM: NavDestination = { id: "system", label: "System", glyph: "⚙" };

const ACCESS_KEYS: NavDestination = { id: "tokens", label: "Access keys", glyph: "⚿" };
const LINK_INVITATIONS: NavDestination = {
  id: "invitations",
  label: "Bank link invitations",
  glyph: "✉",
};
const AGENT_ACCESS: NavDestination = { id: "agent-access", label: "Agent access", glyph: "⚙" };
const APPROVALS: NavDestination = { id: "approval", label: "Approvals", glyph: "✓" };
const SHARED_CARDS: NavDestination = { id: "cards", label: "Shared cards", glyph: "▣" };
const ADD_COMPANY: NavDestination = { id: "add-company", label: "Add a company", glyph: "＋" };
const COMPANY_CHECKLIST: NavDestination = {
  id: "company-checklist",
  label: "Company checklist",
  glyph: "✓",
};
const COMPANY_ONBOARDING: NavDestination = {
  id: "onboarding",
  label: "Company onboarding",
  glyph: "＋",
};
const EXPENSE_AUDIT: NavDestination = {
  id: "expense-audit",
  label: "Expense audit",
  glyph: "⌕",
};
const GROUPS: NavDestination = { id: "groups", label: "Groups", glyph: "☰" };
const ATTACHED_PAYMENTS: NavDestination = {
  id: "attached-payments",
  label: "Attached payments",
  glyph: "▣",
};

/**
 * The four plain groups in the owner's More sheet. Primary destinations are
 * repeated here because the phone bar does not carry all of them.
 */
const MORE_GROUPS: readonly MoreGroup[] = [
  {
    heading: "Money",
    destinations: [BANK_ACCOUNTS, RECEIPTS, RECURRING, INTERCOMPANY],
  },
  {
    // Order is load-bearing: Months follows Reports and Books (the Months
    // scenario pins it), so the new page sits after them.
    heading: "Reports",
    destinations: [REPORTS, BOOKS, MONTHS, TAX_TIME],
  },
  {
    heading: "Review",
    destinations: [WAITING, PROBLEMS, STATEMENT_CHECKS],
  },
  {
    heading: "Settings",
    destinations: [RULES, CONNECTION_STATUS, ADMIN],
  },
];

/** Everything that is not a primary tab or one of the four More groups. */
export const ADMIN_DESTINATIONS: readonly NavDestination[] = [
  ACCESS_KEYS,
  LINK_INVITATIONS,
  AGENT_ACCESS,
  APPROVALS,
  SHARED_CARDS,
  ADD_COMPANY,
  COMPANY_CHECKLIST,
  PRIOR_IMPORT_NAV,
  COMPARE_NAV,
  EXPENSE_AUDIT,
  GROUPS,
  COMPANY_ONBOARDING,
  ATTACHED_PAYMENTS,
];

// ---------------------------------------------------------------------------
// OWNER primary destinations (desktop top bar, before the More control).
// ---------------------------------------------------------------------------
export const OWNER_PRIMARY_NAV: NavDestination[] = [
  HOME,
  TO_REVIEW,
  ACTIVITY,
  BOOKS,
  REPORTS,
  BANK_ACCOUNTS,
];

// ---------------------------------------------------------------------------
// ACCOUNT_MANAGER primary destinations. Same books, To review first.
// defaultRouteForRole(account_manager) stays the queue.
// ---------------------------------------------------------------------------
export const AM_PRIMARY_NAV: NavDestination[] = [
  TO_REVIEW,
  HOME,
  ACTIVITY,
  BOOKS,
  REPORTS,
  BANK_ACCOUNTS,
];

/** Phone bottom bar, before the More control. At most four routes (More is the fifth). */
export const OWNER_PHONE_NAV: NavDestination[] = [HOME, TO_REVIEW, ACTIVITY, BOOKS];

export const AM_PHONE_NAV: NavDestination[] = [TO_REVIEW, HOME, ACTIVITY, BOOKS];

/**
 * Owner-only More/Admin rows. The account manager can read the books; tokens,
 * invitations, groups, cards and agent access stay off the AM surfaces.
 */
export const AM_MORE_EXCLUDED: readonly ShellRoute[] = [
  "tokens",
  "invitations",
  "groups",
  "cards",
  "agent-access",
  "approval",
];

/** Owner System → Workbench destinations (reachable, not center-stage). */
export const OWNER_WORKBENCH_ROUTES = [
  "queue",
  "waiting",
  "statements",
  "receipt-shelf",
  "recurring",
  "attached-payments",
] as const;

const OWNER_ALLOWED = new Set<ShellRoute>([
  "errors",
  "home",
  "balances",
  "transactions",
  "reports",
  "tax-time",
  "intercompany",
  "prior-import",
  "compare",
  "expense-audit",
  "system",
  "general-ledger",
  "months",
  "rules",
  "tokens",
  "invitations",
  "health",
  "groups",
  "cards",
  "onboarding",
  "add-company",
  "company-checklist",
  "agent-access",
  "approval",
  ...OWNER_WORKBENCH_ROUTES,
]);

const AM_ALLOWED = new Set<ShellRoute>([
  "queue",
  "waiting",
  "statements",
  "receipt-shelf",
  "recurring",
  "attached-payments",
  "onboarding",
  "add-company",
  "company-checklist",
  "prior-import",
  "compare",
  "system",
  "health",
  "rules",
  // Approvals and Agent access are owner-only in `ui/capabilities.ts`, so an
  // account manager is not offered either page (B2a-6, E-B4). A deep link still
  // gets the plain "not for you" page; isRouteAllowed is presentation, not
  // authorization. `agent-access` and `approval` stay in AM_MORE_EXCLUDED, so
  // their hidden data-route controls for the deep-link census are unchanged.
  // Account managers can read the books (owner: "Yes, AM can see everything").
  "home",
  "balances",
  "transactions",
  "reports",
  "tax-time",
  "general-ledger",
  "months",
  "intercompany",
  "expense-audit",
  "errors",
]);

const AM_MORE_EXCLUDED_SET = new Set<ShellRoute>(AM_MORE_EXCLUDED);

export const ALL_SHELL_ROUTES = [
  "errors",
  "home",
  "reports",
  "tax-time",
  "intercompany",
  "general-ledger",
  "months",
  "transactions",
  "balances",
  "queue",
  "waiting",
  "statements",
  "receipt-shelf",
  "recurring",
  "attached-payments",
  "groups",
  "cards",
  "rules",
  "tokens",
  "invitations",
  "health",
  "onboarding",
  "add-company",
  "company-checklist",
  "prior-import",
  "compare",
  "expense-audit",
  "agent-access",
  "approval",
  "system",
] as const satisfies readonly ShellRoute[];

type ListedRoute = (typeof ALL_SHELL_ROUTES)[number];
type MissingRoute = Exclude<ShellRoute, ListedRoute>;
type ExtraRoute = Exclude<ListedRoute, ShellRoute>;
const SHELL_ROUTES_COVER_THE_TYPE: [MissingRoute] extends [never] ? true : MissingRoute = true;
const SHELL_ROUTES_ARE_REAL: [ExtraRoute] extends [never] ? true : ExtraRoute = true;
void SHELL_ROUTES_COVER_THE_TYPE;
void SHELL_ROUTES_ARE_REAL;

const SHELL_ROUTE_SET = new Set<string>(ALL_SHELL_ROUTES);

export function isShellRoute(value: string): value is ShellRoute {
  return SHELL_ROUTE_SET.has(value);
}

export function primaryNavForRole(role: StaffRole): NavDestination[] {
  return role === "owner" ? OWNER_PRIMARY_NAV : AM_PRIMARY_NAV;
}

export function phoneNavForRole(role: StaffRole): NavDestination[] {
  return role === "owner" ? OWNER_PHONE_NAV : AM_PHONE_NAV;
}

/**
 * Routes that have no visible control in this role's bar or More sheet still
 * need a data-route control in the DOM (the nav census and deep links depend on
 * it). They live in a hidden container; the Admin page is their visible index.
 */
export function directRoutesForRole(role: StaffRole): NavDestination[] {
  const out: NavDestination[] = [];
  const seen = new Set<ShellRoute>();
  const add = (dest: NavDestination): void => {
    if (seen.has(dest.id)) return;
    seen.add(dest.id);
    out.push(dest);
  };
  for (const dest of adminDestinationsForRole(role)) add(dest);
  add(LEGACY_SYSTEM);
  if (role === "account_manager") {
    for (const dest of ADMIN_DESTINATIONS) {
      if (AM_MORE_EXCLUDED_SET.has(dest.id)) add(dest);
    }
  }
  return out;
}

export function adminDestinationsForRole(role: StaffRole): NavDestination[] {
  if (role === "owner") return [...ADMIN_DESTINATIONS];
  return ADMIN_DESTINATIONS.filter(
    (dest) => isRouteAllowed(role, dest.id) && !AM_MORE_EXCLUDED_SET.has(dest.id),
  );
}

export function moreGroupsForRole(role: StaffRole): MoreGroup[] {
  const groups: MoreGroup[] = [];
  for (const group of MORE_GROUPS) {
    const destinations =
      role === "owner"
        ? group.destinations
        : group.destinations.filter(
            (dest) => !AM_MORE_EXCLUDED_SET.has(dest.id) && isRouteAllowed(role, dest.id),
          );
    if (destinations.length === 0) continue;
    groups.push({ heading: group.heading, destinations });
  }
  return groups;
}

/** Every destination this role can click, primary, More and Admin together. */
export function destinationsForRole(role: StaffRole): NavDestination[] {
  const seen = new Set<ShellRoute>();
  const out: NavDestination[] = [];
  const push = (dest: NavDestination): void => {
    if (seen.has(dest.id)) return;
    seen.add(dest.id);
    out.push(dest);
  };
  for (const dest of primaryNavForRole(role)) push(dest);
  for (const dest of phoneNavForRole(role)) push(dest);
  for (const group of moreGroupsForRole(role)) {
    for (const dest of group.destinations) push(dest);
  }
  for (const dest of adminDestinationsForRole(role)) push(dest);
  return out;
}

export function defaultRouteForRole(role: StaffRole): ShellRoute {
  return role === "owner" ? "home" : "queue";
}

/** True when the route is not one of the role's primary tabs. */
export function moreHostsRoute(role: StaffRole, route: ShellRoute): boolean {
  return !primaryNavForRole(role).some((dest) => dest.id === route);
}

export function systemRoutesForRole(role: StaffRole): Set<ShellRoute> {
  // Kept for the persona-shell contract: Admin owns the agent door for an
  // owner and never for an account manager.
  return role === "owner"
    ? new Set<ShellRoute>(["system", "agent-access", "tokens", "invitations", "groups", "cards"])
    : new Set<ShellRoute>(["system", "health", "rules"]);
}

export function isRouteAllowed(role: StaffRole, route: ShellRoute): boolean {
  return (role === "owner" ? OWNER_ALLOWED : AM_ALLOWED).has(route);
}

/**
 * Fleet-wide chrome — not company-scoped. Home is intentionally excluded:
 * Owner Home uses the active company/scope switcher; the fleet truth strip
 * (banner + badge) stays fleet-wide regardless of Home scope.
 */
export function isFleetRoute(route: ShellRoute): boolean {
  return (
    route === "errors" ||
    route === "queue" ||
    route === "waiting" ||
    route === "tokens" ||
    route === "invitations" ||
    route === "health" ||
    route === "intercompany" ||
    route === "system" ||
    route === "groups" ||
    route === "cards" ||
    route === "onboarding" ||
    route === "add-company" ||
    route === "agent-access" ||
    route === "approval"
  );
}

/** Single-company destinations that honor the group→company landing rule. */
export function isCompanyScopedRoute(route: ShellRoute): boolean {
  return (
    route === "balances" ||
    route === "transactions" ||
    route === "reports" ||
    route === "tax-time" ||
    route === "general-ledger" ||
    route === "months" ||
    route === "rules" ||
    route === "receipt-shelf" ||
    route === "recurring" ||
    route === "attached-payments" ||
    route === "prior-import" ||
    route === "compare" ||
    route === "expense-audit" ||
    route === "company-checklist"
  );
}
